/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { EventDataType } from '@/types/EventDataType.type';
import { useAxios } from '@/hooks';
import { endPoint } from '@/utils';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';

const limit = 10;

type CategoryEventsItem = {
  categoryCode: string;
  events: EventDataType[];
  skip: number;
  hasMore: boolean;
  refreshing: boolean;
  [key: string]: any;
};

const useCategoryEvents = () => {
  const [data, setData] = useState<CategoryEventsItem[] | null>(null);
  const { fetchData, loading } = useAxios(true);
  const location = useSelector((state: RootState) => state.location);
  const coordinates = [location?.lng, location?.lat];

  const updateCategoryItem = (categoryCode: string, newState: any) => {
    setData(
      (prevData) =>
        prevData?.map((item) =>
          item.categoryCode === categoryCode ? { ...item, ...newState } : item,
        ) || prevData,
    );
  };

  const fetchCategoryData = async () => {
    try {
      const res = await fetchData({
        url: endPoint.FETCH_CATEGORY,
        method: 'GET',
        params: {
          limit: limit,
          carouselSection: 'yes',
        },
        body: { coordinates },
      });

      if (res?.data) {
        setData(res.data);

        const fetchAllEvents = async () => {
          const results = await Promise.all(
            res.data.map(async (dataItem: any) => {
              const eventsRes = await fetchData({
                url: endPoint.FETCH_EVENTS_BY_CATEGORY_BY_PAGE_GUEST,
                method: 'POST',
                params: {
                  limit: limit,
                  skip: 0,
                },
                data: {
                  categoryCode: dataItem.categoryCode,
                },
              });
              const events = eventsRes?.data?.events || [];

              return {
                ...dataItem,
                events,
                skip: 0,
                hasMore: events.length > 0 && events.length === limit,
                refreshing: false,
              };
            }),
          );

          setData(results);
        };

        fetchAllEvents();
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  };

  const loadMoreEvents = async (categoryCode: string) => {
    const category = data?.find((item) => item.categoryCode === categoryCode);
    if (
      !category ||
      !category.hasMore ||
      category.refreshing ||
      !category.events?.length
    ) {
      return;
    }

    updateCategoryItem(categoryCode, { refreshing: true });
    const nextSkip = category.skip + limit;

    try {
      const eventsRes = await fetchData({
        url: endPoint.FETCH_EVENTS_BY_CATEGORY_BY_PAGE_GUEST,
        method: 'POST',
        params: {
          limit: limit,
          skip: nextSkip,
        },
        data: {
          categoryCode,
        },
        loading: false,
      });
      const newEvents = eventsRes?.data?.events || [];

      updateCategoryItem(categoryCode, {
        refreshing: false,
        skip: nextSkip,
        hasMore: newEvents.length > 0 && newEvents.length === limit,
        events: [...category.events, ...newEvents],
      });
    } catch (error) {
      console.error('Error fetching more category events:', error);
      updateCategoryItem(categoryCode, { refreshing: false });
    }
  };

  useEffect(() => {
    fetchCategoryData();
  }, [location]);

  return { data, loading, loadMoreEvents, refetch: fetchCategoryData };
};

export default useCategoryEvents;
